import { useCallback, useEffect, useRef, useState } from "react";
import SearchInput from "../components/Forms/SearchInput";
import DataTable from "../components/Tables/Datatable";
import { DYED_TYPES, orderStatus, PERMISSION_IDS, SELECTED_CUSTOMER_STORAGE_KEY } from "../constant";
import { postApi } from "../services/api";
import { Link, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { formatDate, handleRouteNav, numberFormatter, roundDecimal, showAPC, showEstimatedDispatchTime } from "../utils";
import Loader from "../common/Loader";
import Filters from "../components/Filters";
import { useSelector } from "react-redux";
import usePagination from "../hooks/usePagination";
import { CustomDot } from "../components/Charts/CustomBadge";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { Breadcrumb, Select } from "antd";
import AgentClientSelect from "../components/AgentClientSelect";
import PrimaryButton from "../components/Forms/PrimaryButton";

const toAmount = (value) => {
  const numericValue = Number(String(value ?? 0).replace(/,/g, ""));
  return Number.isFinite(numericValue) ? numericValue : 0;
};

export default function SaleBalance() {
  const { appliedFilters, ModalUI, DrawerUI, FilterButton, resetFilters, hasFilters } = Filters({
    filters: [
      {
        type: "daterange",
        key: "order_date",
        label: "Order Placed",
      },
      {
        type: "select",
        key: "item_status",
        label: "Status",
        options: [orderStatus.placed, orderStatus.production, orderStatus.parts_dispatched].map((status) => ({ label: status, value: status })),
      },
    ],
    onChange: () => resetPage(),
    persistToUrl: false,
  });

  const { is_sales_person, is_admin, is_sales_manager, user_id, is_customer, permission_ids, role_id, customer_id, company_name, is_agent } =
    useSelector((state) => state.auth);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [orderData, setOrderData] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(false);
  const { pagination, setPagination, handleChange: handlePaginationChange, resetPage } = usePagination(10, { persist: false });
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const [customerListLoading, setCustomerListLoading] = useState(false);
  const [allCustomers, setAllCustomers] = useState([{ customer_id, company_name }]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(() => {
    const stored = sessionStorage.getItem(SELECTED_CUSTOMER_STORAGE_KEY);
    if (stored === null || stored === "") return customer_id;
    return Number.isNaN(Number(stored)) ? stored : Number(stored);
  });
  const customerSelectRef = useRef(null);

  const location = useLocation();
  const { selectedClient } = location.state || {};

  const toggleExpand = (key) => {
    setExpandedRowKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const noOfRowsToShow = 2;
  const showFirstOnly = false;

  const getVisibleRows = (rows, isExpanded) => {
    if (!rows || rows?.length <= (showFirstOnly ? noOfRowsToShow : noOfRowsToShow * 2)) return rows;
    if (isExpanded) return rows;

    return showFirstOnly
      ? [...rows.slice(0, noOfRowsToShow), { isEllipsis: true, _key: "ellipsis" }]
      : [...rows.slice(0, noOfRowsToShow), { isEllipsis: true, _key: "ellipsis" }, ...rows.slice(-noOfRowsToShow)];
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const fetchIdRef = useRef(0);

  useEffect(() => {
    if (!is_agent) return;

    const fetchCustomersForAgent = async () => {
      setCustomerListLoading(true);
      try {
        const response = await postApi("sales/agent/agent-customer-list", {
          agent_customer_id: customer_id,
        });

        const customers = response?.data || [];
        const hasSelf = customers.some((item) => String(item.customer_id) === String(customer_id));

        const normalizedCustomers = hasSelf
          ? customers
          : [
              {
                customer_id,
                company_name: company_name || "Self",
              },
              ...customers,
            ];

        setAllCustomers(normalizedCustomers);

        const stored = sessionStorage.getItem(SELECTED_CUSTOMER_STORAGE_KEY);
        const normalizedStored = stored === null || stored === "" ? null : Number.isNaN(Number(stored)) ? stored : Number(stored);

        const hasStoredInList = normalizedCustomers.some((item) => String(item.customer_id) === String(normalizedStored));

        const nextCustomerId = hasStoredInList ? normalizedStored : customer_id;

        setSelectedCustomerId(nextCustomerId);
        sessionStorage.setItem(SELECTED_CUSTOMER_STORAGE_KEY, String(nextCustomerId));
      } finally {
        setCustomerListLoading(false);
      }
    };

    fetchCustomersForAgent();
  }, [is_agent, customer_id, company_name]);

  const resetForCustomerChange = () => {
    resetFilters();
    setSearchParams(
      (prev) => {
        prev.delete("q");
        return prev;
      },
      { replace: true },
    );
    setPagination((prev) => ({ ...prev, current: 1 }));
    setOrderData([]);
    setTotalOrders(0);
  };

  const fetchOrders = useCallback(async () => {
    fetchIdRef.current += 1;
    const currentFetchId = fetchIdRef.current;
    try {
      setLoading(true);
      const { order_date, item_status } = appliedFilters;

      const filters = {
        from_date: order_date?.[0] ? formatDate(new Date(order_date[0]), { format: "M/D/YYYY" }) : undefined,
        to_date: order_date?.[1] ? formatDate(new Date(order_date[1]), { format: "M/D/YYYY" }) : undefined,
        status: item_status || undefined,
      };

      const response = await postApi(
        "credit/credit-list",
        {
          search: query,
          page: pagination.current,
          per_page: pagination.pageSize,
          ...(Object.keys(appliedFilters).length ? filters : {}),
          ...(is_sales_person && !permission_ids.includes(PERMISSION_IDS.VIEW_ALL_OUTSTANDING) ? { user_id } : {}),
          ...(is_customer ? { customer_id: is_agent === 1 ? selectedCustomerId || customer_id : selectedClient || customer_id } : {}),
        },
        null,
      );

      if (currentFetchId !== fetchIdRef.current) {
        return;
      }

      const formattedRows = (response?.data || []).map((party, index) => {
        let partyTotalBalCases = 0;
        let partyTotalBalMeters = 0;
        let partyTotalDispatchedCases = 0;
        let partyTotalDispatchedMeters = 0;
        let partyTotalCases = 0;
        let partyTotalMeters = 0;
        const displayList = [];

        (party.orders || []).forEach((order) => {
          let orderTotalCases = 0;
          let orderTotalMeters = 0;
          let orderBalCases = 0;
          let orderBalMeters = 0;
          let orderDispatchedCases = 0;
          let orderDispatchedMeters = 0;

          (order.items || []).forEach((item, i) => {
            const cases = Number(item.update_total_cases || item.cases || 0);
            const meters = Number(item.update_total_meter || item.quantity_meters || 0);
            const balCases = Number(item.balance_cases_no || 0);
            const balMeters = Number(item.balance_qty || 0);
            const dispatchedCases = Number(item.dispatch_cases_no || 0);
            const dispatchedMeters = Number(item.dispatch_qty || 0);

            orderTotalCases += cases;
            orderTotalMeters += meters;
            orderBalCases += balCases || cases;
            orderBalMeters += balMeters || meters;
            orderDispatchedCases += dispatchedCases;
            orderDispatchedMeters += dispatchedMeters;

            const estimateDispatch =
              showEstimatedDispatchTime(item, {
                getLength: 1,
                skipFullStop: true,
              }) || "-";

            displayList.push({
              ...item,
              type: "item",
              _key: `item-${order.order_id}-${item.order_item_id || i}`,
              orderId: order.order_id,
              shortName: item.short_name,
              shadeNo: item.shade_no,
              cases,
              meters,
              balCases: balCases || cases,
              balMeters: roundDecimal(balMeters || meters),
              status: item.status || "-",
              dispatchedCases,
              dispatchedMeters: roundDecimal(dispatchedMeters),
              estimateDispatch,
            });
          });

          partyTotalBalCases += orderBalCases;
          partyTotalBalMeters += orderBalMeters;
          partyTotalDispatchedCases += orderDispatchedCases;
          partyTotalDispatchedMeters += orderDispatchedMeters;
          partyTotalMeters += orderTotalMeters;
          partyTotalCases += orderTotalCases;

          displayList.push({
            ...order,
            type: "order_total",
            _key: `total-${order.order_id}`,
            orderId: order.order_id,
            cases: orderTotalCases,
            meters: roundDecimal(orderTotalMeters),
            balCases: orderBalCases,
            balMeters: roundDecimal(orderBalMeters),
            dispatchedCases: orderDispatchedCases,
            dispatchedMeters: roundDecimal(orderDispatchedMeters),
          });
        });

        return {
          ...party,
          key: party.party_code || index,
          partyCode: party.party_code || "-",
          partyName: party.company_name || "-",
          displayList,
          partyTotalBalCases,
          partyTotalBalMeters: roundDecimal(partyTotalBalMeters),
          partyTotalDispatchedCases,
          partyTotalDispatchedMeters: roundDecimal(partyTotalDispatchedMeters),
          partyTotalCases,
          partyTotalMeters: roundDecimal(partyTotalMeters),
        };
      });

      setOrderData(formattedRows);
      setTotalOrders(response?.total || 0);
    } catch (error) {
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
    } finally {
      setExpandedRowKeys([]);
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
      setLoading(false);
    }
  }, [
    appliedFilters,
    is_sales_person,
    pagination.current,
    pagination.pageSize,
    query,
    user_id,
    is_customer,
    is_agent,
    selectedCustomerId,
    selectedClient,
    customer_id,
  ]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchOrders();
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [fetchOrders]);

  const showAssignedSales = is_sales_manager || is_admin || (is_sales_person && permission_ids.includes(PERMISSION_IDS.VIEW_ALL_OUTSTANDING));

  const renderCell = (record, renderItem, renderTotal, footerContent, hideTotalsAndFooter = false) => {
    const isExpanded = expandedRowKeys.includes(record.key);
    const visibleRows = getVisibleRows(record.displayList, isExpanded);

    return (
      <div className="flex flex-col gap-1">
        {visibleRows.length === 0 ? (
          <div className="text-slate-500">-</div>
        ) : (
          visibleRows.map((row, index) => {
            // Handle ellipsis
            if (row.isEllipsis) {
              return (
                <div
                  key={`ell-${record.key}-${index}`}
                  className={`${hideTotalsAndFooter ? "text-transparent" : "text-slate-400"} font-bold tracking-widest leading-relaxed`}
                >
                  ...
                </div>
              );
            }

            // Render the Order Total at the BOTTOM of its items, acting as a divider
            if (row.type === "order_total") {
              // If it's the very last total before the footer or an ellipsis, we omit the divider so it doesn't double-stack with the Party Footer border.
              const isLastTotal = index === visibleRows.length - 1 || visibleRows[index + 1]?.isEllipsis;

              return (
                <div
                  key={row._key}
                  className={`font-bold mt-1 ${isLastTotal ? "mb-1" : "mb-3 pb-3 border-b border-slate-200"} ${hideTotalsAndFooter ? "text-transparent" : "text-slate-900"} truncate`}
                >
                  {hideTotalsAndFooter ? "-" : renderTotal(row)}
                </div>
              );
            }

            // Render the Standard Item
            return (
              <div key={row._key} className="flex flex-col">
                {renderItem(row)}
              </div>
            );
          })
        )}

        <div
          className={`font-bold mt-3 border-t-2 pt-2 uppercase ${hideTotalsAndFooter ? "text-transparent border-transparent" : "text-slate-900 border-slate-300"}`}
        >
          {hideTotalsAndFooter ? "-" : footerContent}
        </div>
      </div>
    );
  };

  const columns = [
    {
      title: "Client",
      key: "party",
      dataIndex: "company_name",
      width: 220,
      ellipsis: true,
      onCell: () => ({ style: { verticalAlign: "top" } }),
      render: (_, record) => {
        return (
          <div className="flex flex-col gap-1 text-left">
            <div className="flex gap-1 flex-col text-start">
              <span className="font-semibold text-slate-900 truncate" title={record.company_name || "-"}>
                {record.company_name || "-"}
              </span>
              {showAssignedSales && (
                <span className="text-sm text-slate-500 truncate" title={record.assigned_sale_person || "-"}>
                  {record.assigned_sale_person || "-"}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Item Details",
      key: "orderDetails",
      width: 250,
      onCell: () => ({ style: { verticalAlign: "top" } }),
      render: (_, record) =>
        renderCell(
          record,
          (row) => (
            <div
              className="text-slate-600 flex items-start min-w-0"
              title={`#${row.orderId} - ${row.shortName} (${row.shadeNo}) ${row.dye_type == DYED_TYPES.piece ? "PD" : "TD"}`}
            >
              <span className="shrink-0 whitespace-nowrap">#{row.orderId} -&nbsp;</span>
              <span className="truncate">{row.shortName}</span>
              <span className="shrink-0 whitespace-nowrap">
                &nbsp;({row.shadeNo}
                {showAPC(row.apc_no, false)}) {row.dye_type == DYED_TYPES.piece ? "PD" : "TD"}
              </span>
            </div>
          ),
          (totalRow) => `Order #${totalRow.orderId} Total`,
          "TOTAL BALANCE",
        ),
    },
    {
      title: "Balance Qty",
      key: "balanceQty",
      onCell: () => ({
        className: "highlight-balance-col",
        style: { verticalAlign: "top" },
      }),
      render: (_, record) =>
        renderCell(
          record,
          (row) => <div className="text-slate-900 truncate">{`${row.balCases} cases (${row.balMeters}m)`}</div>,
          (totalRow) => `${totalRow.balCases} cases (${totalRow.balMeters}m)`,
          `${record.partyTotalBalCases} cases (${record.partyTotalBalMeters}m)`,
        ),
    },
    {
      title: "Dispatched Qty",
      key: "dispatchQty",
      onCell: () => ({ style: { verticalAlign: "top" } }),
      render: (_, record) =>
        renderCell(
          record,
          (row) => <div className="text-slate-900 truncate">{`${row.dispatchedCases} cases (${row.dispatchedMeters}m)`}</div>,
          (totalRow) => `${totalRow.dispatchedCases} cases (${totalRow.dispatchedMeters}m)`,
          `${record.partyTotalDispatchedCases} cases (${record.partyTotalDispatchedMeters}m)`,
        ),
    },
    {
      title: "Ordered Qty",
      key: "orderedQty",
      onCell: () => ({ style: { verticalAlign: "top" } }),
      render: (_, record) =>
        renderCell(
          record,
          (row) => <div className="text-slate-900 truncate">{`${row.cases} cases (${row.meters}m)`}</div>,
          (totalRow) => `${totalRow.cases} cases (${totalRow.meters}m)`,
          `${record.partyTotalCases || 0} cases (${record.partyTotalMeters || 0}m)`,
        ),
    },
    {
      title: "Status",
      key: "status",
      onCell: () => ({ style: { verticalAlign: "top" } }),
      render: (_, record) => renderCell(record, (row) => <div className="text-slate-500 truncate">{row.status}</div>, null, null, true),
    },
    {
      title: "Order Placed",
      key: "created_at",
      onCell: () => ({ style: { verticalAlign: "top" } }),
      render: (_, record) =>
        renderCell(record, (row) => <div className="text-slate-500 truncate">{formatDate(row.created_at)}</div>, null, null, true),
    },
    {
      title: "Estimated Dispatch",
      key: "estimateDispatch",
      onCell: () => ({ style: { verticalAlign: "top" } }),
      render: (_, record) =>
        renderCell(record, (row) => <div className="text-slate-500 truncate">{row.estimateDispatch || "-"}</div>, null, null, true),
    },
    {
      title: "Comments",
      key: "comments",
      onCell: () => ({ style: { verticalAlign: "top" } }),
      render: (_, record) => renderCell(record, (row) => <div className="text-slate-500 truncate">{row.comments || "-"}</div>, null, null, true),
    },
    {
      title: "Trade Level",
      key: "tdlevel_name",
      onCell: () => ({ style: { verticalAlign: "top" } }),
      render: (_, record) => renderCell(record, (row) => <div className="text-slate-500 truncate">{row.tdlevel_name || "-"}</div>, null, null, true),
    },
    {
      title: "",
      key: "actions",
      width: 50,
      fixed: "right",
      onCell: () => ({ style: { verticalAlign: "top" } }),
      render: (_, record) => {
        const rowsLength = (record.displayList || []).length;
        const isExpanded = expandedRowKeys.includes(record.key);

        return rowsLength > (showFirstOnly ? noOfRowsToShow : noOfRowsToShow * 2) ? (
          <div
            onClick={() => toggleExpand(record.key)}
            className="flex items-center hover:underline underline-offset-2 gap-1 w-[110px] cursor-pointer transition-opacity hover:opacity-80 text-[#A33434]"
          >
            <span className="text-[13px] font-medium ">View {isExpanded ? "Less" : "More"}</span>
            {isExpanded ? <FaChevronUp className="text-[12px]" /> : <FaChevronDown className="text-[12px]" />}
          </div>
        ) : null;
      },
    },
  ].filter(Boolean);

  const handleChange = (paginationInfo, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    handlePaginationChange(paginationInfo, filters, sorter);
  };

  const onSearch = (value, _e, info) => console.log(info?.source, value);

  const handleOnSearchChange = (e) => {
    const value = e.target.value;
    setSearchParams(
      (prev) => {
        if (value) prev.set("q", value);
        else prev.delete("q");
        return prev;
      },
      { replace: true },
    );
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleRow = (record) => {};

  const { setTitle, setDescription, setBackButton } = useOutletContext();

  useEffect(() => {
    setTitle("Sale Balance");
    setBackButton(true);
    setDescription(
      <div>
        <Breadcrumb>
          {[
            { title: "Home", href: handleRouteNav("", role_id) },
            {
              title: is_customer ? "My Orders" : "Orders",
              href: handleRouteNav("/orders", role_id),
            },
            {
              title: "Sale Balance",
              href: handleRouteNav(`/orders/sale-balance`, role_id),
            },
          ].map((item, index) => (
            <Breadcrumb.Item key={index}>
              <Link to={item.href}>{item.title}</Link>
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
      </div>,
    );
  }, [setTitle, setDescription, setBackButton, totalOrders]);

  return (
    <>
      <ModalUI />
      <div className="bg-[#FFFFFF]" style={{ borderRadius: "32px", overflow: "hidden" }}>
        <div className="flex justify-between gap-7 items-center px-8 py-6">
          {/* <AgentClientSelect
            selectedCustomerId={selectedCustomerId}
            onClientChange={(newId) => {
              setSelectedCustomerId(newId);
              resetForCustomerChange();
            }}
          /> */}
          <div className="flex items-center gap-3 w-full">
            <SearchInput onSearch={onSearch} value={query} onChange={handleOnSearchChange} placeholder="Search for items" />
          </div>
          <div className="flex gap-3 items-center">
            <FilterButton />
            {hasFilters && is_customer && (
              <PrimaryButton btnType="secondary" text="Remove" onClick={() => resetFilters()} textClassName="" className="!w-[130px]" />
            )}
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader size={60} />
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={orderData}
            handleChange={handleChange}
            handleRow={handleRow}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: totalOrders,
              showSizeChanger: true,
            }}
            className={`no-row-hover ${is_customer ? "pb-12" : ""}`}
            Showpagination={!is_customer}
          />
        )}
      </div>
    </>
  );
}
